import React, { useState, useEffect } from 'react';
import { readGroups, fetchGroups } from '../../utils/groupCache';
import { IconCalendar } from '../../components/Icons';
import BackButton from '../../components/BackButton';
import { guildImg } from '../../utils/guildImage';

interface SiegeGroupData {
  _id: string;
  groupName: string;
  opponentGuilds: string[];
  startDate: string;
  endDate?: string;
  /** 길드 이름 → 그 판 순위(1~3). 매치업 현황 패킷을 받은 판만 있다(서버 `/siege-groups/groups`) */
  ranks?: Record<string, number>;
}

interface GroupSelectProps {
  season: number;
  onSelect: (group: string) => void;
  onBack: () => void;
  groups?: SiegeGroupData[]; // 외부에서 전달된 그룹 데이터
  showBackButton?: boolean; // 뒤로가기 버튼 표시 여부
  /**
   * 그룹별 통계. 서버가 그룹 목록에 실어 준 집계값이다(기록을 받지 않아도 된다).
   *
   *   wins/losses  지금 보고 있는 종류(공격/방어) 기준
   *   winRate      우리가 공격해서 이긴 비율
   *   defenseRate  우리 방덱이 막아낸 비율
   */
  groupStats?: {
    [groupName: string]: { wins: number; losses: number; winRate?: number; defenseRate?: number };
  };
}

/**
 * 카드 안의 길드 한 줄(엠블럼 + 이름).
 *
 * 우리 길드와 상대 두 곳이 같은 모양인데 예전에는 같은 인라인 스타일 덩어리가 세 번
 * 되풀이돼 있었다. 한 곳만 고치면 나머지가 어긋나므로 부품으로 뺀다.
 */
const GuildRow: React.FC<{
  name: string;
  /** 우리 길드면 보라색 강조 */
  mine?: boolean;
  /** 그 판 순위 — 있으면 이름 오른쪽에 메달 색 알약으로(사용자) */
  rank?: number;
  isMobile: boolean;
}> = ({ name, mine = false, rank, isMobile }) => {
  const size = isMobile ? 'clamp(14px, 3vw, 16px)' : 'clamp(30px, 4.6vw, 36px)';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 4 : 'clamp(6px, 1.2vw, 8px)',
        padding: isMobile ? '3px 4px' : 'clamp(7px, 1.4vw, 9px) clamp(9px, 1.8vw, 11px)',
        background: mine ? 'rgba(102, 126, 234, 0.09)' : 'rgba(255, 255, 255, 0.75)',
        borderRadius: isMobile ? 5 : 'clamp(7px, 1.4vw, 9px)',
        border: `1px solid ${mine ? 'rgba(102, 126, 234, 0.25)' : 'rgba(0, 0, 0, 0.06)'}`,
        minWidth: 0
      }}
    >
      <img loading="lazy"
        src={guildImg(name)}
        alt={name}
        style={{
          width: size,
          height: size,
          borderRadius: isMobile ? 3 : 'clamp(5px, 1vw, 7px)',
          objectFit: 'cover',
          border: `1px solid ${mine ? '#667eea' : 'rgba(0, 0, 0, 0.1)'}`,
          flexShrink: 0
        }}
        onError={(e) => {
          e.currentTarget.src = '/images/unknown2.png';
        }}
      />
      <div
        style={{
          // 모바일 글자를 키웠다 — 예전에는 6~7px 라 사실상 읽을 수 없었다
          fontSize: isMobile ? 'clamp(8px, 2vw, 10px)' : 'clamp(13px, 2.4vw, 15px)',
          color: mine ? '#667eea' : '#555',
          fontWeight: 700,
          letterSpacing: '-0.2px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0
        }}
      >
        {name}
      </div>
      {rank ? (
        <span style={{
          marginLeft: 'auto', flexShrink: 0,
          background: ['#f5b301', '#9aa5b1', '#c7814a'][rank - 1] || '#9ca3af', color: '#fff',
          fontSize: isMobile ? 'clamp(7px, 1.7vw, 8px)' : 'clamp(11px, 2vw, 12px)', fontWeight: 900,
          padding: isMobile ? '0 4px' : 'clamp(2px, 0.4vw, 3px) clamp(7px, 1.3vw, 9px)', borderRadius: 999,
          boxShadow: '0 1px 4px rgba(0,0,0,0.15)', whiteSpace: 'nowrap'
        }}>{rank}위</span>
      ) : null}
    </div>
  );
};

/**
 * 카드 아래에 붙는 성적 띠.
 *
 * 예전에는 승/패 알약을 **우리 길드 이름 옆**에 끼워 넣어서 이름과 숫자가 한 칸을
 * 다퉜다. 승률·방어율까지 들어오니 더 뭉개졌다. 카드 맨 아래로 내려 제 자리를 준다.
 */
const StatsStrip: React.FC<{
  stats: { wins: number; losses: number; winRate?: number; defenseRate?: number };
  isMobile: boolean;
}> = ({ stats, isMobile }) => {
  const total = stats.wins + stats.losses;
  const rate = typeof stats.winRate === 'number' ? stats.winRate : total ? (stats.wins / total) * 100 : 0;
  const pct = (v?: number) => (typeof v === 'number' ? `${v.toFixed(1)}%` : '-');
  const small = isMobile ? 'clamp(7px, 1.7vw, 8px)' : 'clamp(10px, 1.8vw, 12px)';

  return (
    <div
      style={{
        marginTop: isMobile ? 4 : 'clamp(9px, 1.8vw, 12px)',
        paddingTop: isMobile ? 4 : 'clamp(7px, 1.4vw, 9px)',
        borderTop: '1px solid rgba(102, 126, 234, 0.14)',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 3 : 5
      }}
    >
      {/* 승률 막대. 숫자만 있을 때보다 카드끼리 견주기 쉽다 */}
      <div
        style={{
          height: isMobile ? 3 : 5,
          borderRadius: 999,
          background: 'rgba(244, 67, 54, 0.18)',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            width: `${Math.max(0, Math.min(100, rate))}%`,
            height: '100%',
            borderRadius: 999,
            background: 'linear-gradient(90deg, #42a5f5 0%, #2196F3 100%)'
          }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 4 }}>
        <span style={{ fontSize: small, fontWeight: 800, color: '#2196F3', whiteSpace: 'nowrap' }}>
          {stats.wins}
          <span style={{ color: '#9e9e9e', fontWeight: 700 }}>승 </span>
          <span style={{ color: '#f44336' }}>{stats.losses}</span>
          <span style={{ color: '#9e9e9e', fontWeight: 700 }}>패</span>
        </span>
        {/* 라벨 없이 숫자만 둔다. 앞이 승률, 뒤가 방어율이고 **색으로 가른다** —
            글자를 넣으면 카드가 좁아 줄이 넘친다. 어느 쪽인지는 마우스를 올리면 나온다 */}
        <span style={{ fontSize: small, fontWeight: 700, color: '#8b7355', whiteSpace: 'nowrap' }}>
          <span title="승률 (우리가 공격해서 이긴 비율)">{pct(stats.winRate)}</span>
          {typeof stats.defenseRate === 'number' && (
            <>
              <span style={{ color: '#ddd', margin: '0 3px' }}>|</span>
              <span style={{ color: '#7c3aed' }} title="방어율 (우리 방덱이 막아낸 비율)">
                {pct(stats.defenseRate)}
              </span>
            </>
          )}
        </span>
      </div>
    </div>
  );
};

const GroupSelect: React.FC<GroupSelectProps> = ({ season, onSelect, onBack, groups: propsGroups, showBackButton = true, groupStats }) => {
  const [groups, setGroups] = useState<SiegeGroupData[]>([]);
  const [loading, setLoading] = useState(true); // 로딩 상태 추가
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // 화면 크기 변경 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // 부모가 이미 받아 왔으면 그것을 쓴다
    if (propsGroups && propsGroups.length) {
      setGroups(propsGroups);
      setLoading(false);
      return;
    }

    /**
     * **저장된 카드를 먼저 그린다.**
     *
     * 시즌을 고르면 카드가 뜨기까지 빈 화면이 보였다(NAS 왕복). 지난번에 본 목록이
     * 탭에 남아 있으면 그걸로 바로 그리고, 아래에서 새로 받아 갱신한다.
     */
    const cached = readGroups(season);
    if (cached) {
      setGroups(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }

    // 부모가 받아오는 중이면(빈 배열을 넘겨 주었으면) 우리는 요청하지 않는다
    if (propsGroups) return;

    fetchGroups(season)
      .then((gs) => {
        setGroups(gs);
        setLoading(false);
      })
      .catch(() => {
        // 저장된 값이 있으면 그걸로 버틴다
        if (!cached) setGroups([]);
        setLoading(false);
      });
  }, [season, propsGroups]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <>
      <style>
        {`
          @keyframes pulse-glow-group {
            0%, 100% {
              box-shadow: 0 10px 40px rgba(102, 126, 234, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8), 0 0 0 0 rgba(102, 126, 234, 0);
            }
            50% {
              box-shadow: 0 10px 40px rgba(102, 126, 234, 0.25), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8), 0 0 20px 4px rgba(102, 126, 234, 0.4);
            }
          }

          @keyframes badge-pulse-group {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.05);
            }
          }
        `}
      </style>
      {showBackButton && (
        <div style={{ marginBottom: 'clamp(20px, 4vw, 32px)', display: 'flex', justifyContent: 'flex-start' }}>
          <BackButton onClick={onBack} />
        </div>
      )}

      {/* 로딩 인디케이터 */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'clamp(200px, 40vh, 300px)',
          width: '100%',
          marginTop: showBackButton ? 0 : 'clamp(40px, 8vw, 60px)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'clamp(8px, 2vw, 12px)',
            color: '#667eea',
            fontSize: isMobile ? 'clamp(12px, 2.5vw, 14px)' : 'clamp(14px, 2.5vw, 16px)',
            fontWeight: 600
          }}>
            <div style={{
              width: isMobile ? 'clamp(20px, 4vw, 24px)' : 'clamp(24px, 4vw, 28px)',
              height: isMobile ? 'clamp(20px, 4vw, 24px)' : 'clamp(24px, 4vw, 28px)',
              border: '3px solid rgba(102, 126, 234, 0.2)',
              borderTopColor: '#667eea',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span>불러오는 중...</span>
            <style>{`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      <div style={{
        display: loading ? 'none' : 'grid',
        gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
        gap: isMobile ? 'clamp(12px, 2.5vw, 16px) clamp(6px, 1.5vw, 8px)' : 'clamp(20px, 4vw, 28px) clamp(16px, 3vw, 20px)',
        width: '100%',
        marginTop: showBackButton ? 0 : 'clamp(40px, 8vw, 60px)',
        padding: isMobile ? '0 clamp(4px, 1vw, 6px)' : '0',
        justifyItems: 'center'
      }}>
        {/* 전체 카드 - 항상 표시 */}
        {!loading && (
          <div
            onClick={() => onSelect('all')}
            style={{
              background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%)',
              borderRadius: isMobile ? 'clamp(6px, 1.5vw, 8px)' : 'clamp(12px, 2.4vw, 16px)',
              padding: isMobile ? '7px 6px' : 'clamp(16px, 3vw, 20px) clamp(12px, 2.4vw, 16px)',
              cursor: 'pointer',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 10px 40px rgba(102, 126, 234, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isMobile ? 'clamp(78px, 16vw, 96px)' : 'clamp(150px, 25vw, 190px)',
              width: '100%',
              border: isMobile ? '1px solid transparent' : '2px solid transparent',
              backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%), linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
              e.currentTarget.style.boxShadow = '0 20px 60px rgba(102, 126, 234, 0.25), 0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0) scale(1)';
              e.currentTarget.style.boxShadow = '0 10px 40px rgba(102, 126, 234, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8)';
            }}
          >

            {/* 전체 텍스트 */}
            <h2 style={{
              color: '#667eea',
              fontSize: isMobile ? 'clamp(10px, 2.5vw, 12px)' : 'clamp(24px, 4.5vw, 28px)',
              fontWeight: 900,
              margin: 0,
              textAlign: 'center',
              position: 'relative',
              zIndex: 1,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>전체</h2>
          </div>
        )}

        {/* 그룹 카드들 */}
        {groups.map((group, index) => {
          const isLatestGroup = index === 0;
          return (
            <div
              key={group._id}
              onClick={() => onSelect(group.groupName)}
              style={{
                background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%)',
                borderRadius: isMobile ? 'clamp(6px, 1.5vw, 8px)' : 'clamp(12px, 2.4vw, 16px)',
                padding: isMobile ? '7px 6px' : 'clamp(16px, 3vw, 20px) clamp(12px, 2.4vw, 16px)',
                cursor: 'pointer',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 10px 40px rgba(102, 126, 234, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                justifyContent: 'center',
                minHeight: isMobile ? 'clamp(78px, 16vw, 96px)' : 'clamp(150px, 25vw, 190px)',
                width: '100%',
                border: isMobile ? '1px solid transparent' : '2px solid transparent',
                backgroundImage: 'linear-gradient(145deg, rgba(255, 255, 255, 0.98) 0%, rgba(245, 247, 255, 0.98) 100%), linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                position: 'relative',
                overflow: 'visible',
                animation: isLatestGroup ? 'pulse-glow-group 2s ease-in-out infinite' : 'none'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 20px 60px rgba(102, 126, 234, 0.25), 0 4px 16px rgba(0, 0, 0, 0.1), inset 0 1px 2px rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.animation = 'none';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 10px 40px rgba(102, 126, 234, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06), inset 0 1px 2px rgba(255, 255, 255, 0.8)';
                e.currentTarget.style.animation = isLatestGroup ? 'pulse-glow-group 2s ease-in-out infinite' : 'none';
              }}
            >

            {/* NEW 배지 또는 날짜 배지 (오른쪽 위) */}
            {isLatestGroup ? (
              <div style={{
                position: 'absolute',
                top: isMobile ? 'clamp(-5px, -1vw, -6px)' : 'clamp(-8px, -1.6vw, -10px)',
                right: isMobile ? 'clamp(3px, 0.8vw, 4px)' : 'clamp(8px, 1.6vw, 10px)',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: 'white',
                padding: isMobile ? '1px clamp(3px, 0.8vw, 4px)' : 'clamp(4px, 0.8vw, 5px) clamp(8px, 1.6vw, 10px)',
                borderRadius: isMobile ? 'clamp(6px, 1.5vw, 8px)' : 'clamp(10px, 2vw, 14px)',
                fontSize: isMobile ? 'clamp(6px, 1.5vw, 7px)' : 'clamp(9px, 1.8vw, 10px)',
                fontWeight: 800,
                boxShadow: '0 4px 12px rgba(255, 107, 107, 0.4)',
                letterSpacing: '0.5px',
                zIndex: 10,
                animation: 'badge-pulse-group 1.5s ease-in-out infinite'
              }}>
                NEW
              </div>
            ) : (
              group.startDate && (
                <div style={{
                  position: 'absolute',
                  top: isMobile ? 'clamp(-5px, -1vw, -6px)' : 'clamp(-8px, -1.6vw, -10px)',
                  right: isMobile ? 'clamp(3px, 0.8vw, 4px)' : 'clamp(8px, 1.6vw, 10px)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  padding: isMobile ? '1px clamp(3px, 0.8vw, 4px)' : 'clamp(4px, 0.8vw, 5px) clamp(8px, 1.6vw, 10px)',
                  borderRadius: isMobile ? 'clamp(6px, 1.5vw, 8px)' : 'clamp(10px, 2vw, 14px)',
                  fontSize: isMobile ? 'clamp(6px, 1.5vw, 7px)' : 'clamp(9px, 1.8vw, 10px)',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '1px' : 'clamp(3px, 0.6vw, 4px)',
                  zIndex: 10
                }}>
                  {!isMobile && <span style={{ fontSize: 'clamp(10px, 2vw, 11px)' }}><IconCalendar /></span>}
                  {formatDate(group.startDate)}
                  {group.endDate && !isMobile && ` ~ ${formatDate(group.endDate)}`}
                </div>
              )
            )}

            {/* 우리 길드 → VS → 상대 두 곳. 세 줄이 같은 모양이라 부품 하나로 그린다 */}
            <GuildRow name="설탕" mine rank={group.ranks?.['설탕']} isMobile={isMobile} />

            {/* VS 구분선 */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 3 : 'clamp(5px, 1vw, 7px)',
              margin: isMobile ? '3px 0' : 'clamp(5px, 1vw, 7px) 0'
            }}>
              <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(102, 126, 234, 0.3), transparent)' }} />
              <span style={{
                fontSize: isMobile ? 'clamp(7px, 1.7vw, 8px)' : 'clamp(10px, 1.9vw, 12px)',
                fontWeight: 800,
                color: '#667eea',
                padding: isMobile ? '1px 4px' : 'clamp(2px, 0.5vw, 3px) clamp(6px, 1.2vw, 8px)',
                background: 'rgba(102, 126, 234, 0.1)',
                borderRadius: 999,
                letterSpacing: '0.5px'
              }}>VS</span>
              <div style={{ flex: 1, height: 1, background: 'linear-gradient(to right, transparent, rgba(102, 126, 234, 0.3), transparent)' }} />
            </div>

            {/* 상대 길드들 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 3 : 'clamp(5px, 1vw, 7px)' }}>
              {(group.opponentGuilds || []).slice(0, 2).map((opp) => (
                <GuildRow key={opp} name={opp} rank={group.ranks?.[opp]} isMobile={isMobile} />
              ))}
            </div>

            {/* 성적은 맨 아래 띠로. 길드 이름과 자리를 다투지 않는다 */}
            {groupStats && groupStats[group.groupName] && (
              <StatsStrip stats={groupStats[group.groupName]} isMobile={isMobile} />
            )}
          </div>
        );
        })}
      </div>
    </>
  );
};

export default GroupSelect;
